import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
// Storage adapter that works for both web and mobile
const createStorage = () => {
    if (typeof window !== 'undefined') {
        // Web environment
        return createJSONStorage(() => localStorage);
    }
    else {
        // React Native environment - will need AsyncStorage
        try {
            const AsyncStorage = require('@react-native-async-storage/async-storage').default;
            return createJSONStorage(() => AsyncStorage);
        }
        catch {
            // Fallback if AsyncStorage is not available
            return createJSONStorage(() => ({
                getItem: () => Promise.resolve(null),
                setItem: () => Promise.resolve(),
                removeItem: () => Promise.resolve(),
            }));
        }
    }
};
export const useWishlistStore = create()(persist((set, get) => ({
    // Initial state
    items: [],
    isLoading: false,
    error: null,
    // Actions
    addToWishlist: (equipmentId, equipment) => {
        const state = get();
        const exists = state.items.some(item => item.equipmentId === equipmentId);
        if (!exists) {
            const newItem = {
                equipmentId,
                addedAt: new Date().toISOString(),
                ...(equipment && { equipment }),
            };
            set({
                items: [...state.items, newItem],
                error: null,
            });
        }
    },
    removeFromWishlist: (equipmentId) => {
        const state = get();
        set({
            items: state.items.filter(item => item.equipmentId !== equipmentId),
            error: null,
        });
    },
    isInWishlist: (equipmentId) => {
        const state = get();
        return state.items.some(item => item.equipmentId === equipmentId);
    },
    toggleWishlist: (equipmentId, equipment) => {
        const state = get();
        const isCurrentlyInWishlist = state.isInWishlist(equipmentId);
        if (isCurrentlyInWishlist) {
            state.removeFromWishlist(equipmentId);
            return false;
        }
        else {
            state.addToWishlist(equipmentId, equipment);
            return true;
        }
    },
    clearWishlist: () => {
        set({
            items: [],
            error: null,
        });
    },
    getWishlistCount: () => {
        const state = get();
        return state.items.length;
    },
    getWishlistItems: () => {
        const state = get();
        return state.items;
    },
    // Future server sync functionality
    syncWithServer: async () => {
        set({ isLoading: true, error: null });
        try {
            // TODO: Implement server sync
            // const response = await apiClient.syncWishlist(get().items);
            // set({ items: response.items });
        }
        catch (error) {
            set({ error: 'Failed to sync wishlist with server' });
        }
        finally {
            set({ isLoading: false });
        }
    },
}), {
    name: 'coffeeplexity-wishlist',
    storage: createStorage(),
    version: 1,
    migrate: (persistedState, version) => {
        // Handle migrations for future versions
        if (version === 0) {
            // Migrate from version 0 to 1
            return {
                ...persistedState,
                error: null,
            };
        }
        return persistedState;
    },
}));
// Convenience hooks for common operations
export const useWishlist = () => {
    const store = useWishlistStore();
    return {
        items: store.items,
        isLoading: store.isLoading,
        error: store.error,
        count: store.getWishlistCount(),
        addToWishlist: store.addToWishlist,
        removeFromWishlist: store.removeFromWishlist,
        isInWishlist: store.isInWishlist,
        toggleWishlist: store.toggleWishlist,
        clearWishlist: store.clearWishlist,
    };
};
export const useWishlistItem = (equipmentId) => {
    const isInWishlist = useWishlistStore(state => state.isInWishlist(equipmentId));
    const toggleWishlist = useWishlistStore(state => state.toggleWishlist);
    const addToWishlist = useWishlistStore(state => state.addToWishlist);
    const removeFromWishlist = useWishlistStore(state => state.removeFromWishlist);
    return {
        isInWishlist,
        toggle: (equipment) => toggleWishlist(equipmentId, equipment),
        add: (equipment) => addToWishlist(equipmentId, equipment),
        remove: () => removeFromWishlist(equipmentId),
    };
};
